import { getTriageQuestions } from '@cannect/services/resources/instantConsultation/requests'
import { TTriageQuestion } from '@cannect/services/resources/instantConsultation/types'
import { useBrand } from 'hooks/useBrand'
import { ArrowLeft, Loader2, ClipboardList, Zap } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'

type SchedulingTriageStepProps = {
  patientUserId: number
  onComplete: () => void
  onBack: () => void
}

// Mesma normalização do questionário da Consulta na Hora (QuestionnaireStep):
// id 4 vira múltipla escolha sobre uso prévio de cannabis e id 2 vira escala 0-10.
function normalizeQuestions(raw: TTriageQuestion[]): TTriageQuestion[] {
  return raw.map((question) => {
    if (question.id === 4) {
      return {
        ...question,
        type: 'options' as const,
        options: ['Não, nunca usei.', 'Sim, e nunca tive nenhum efeito adverso. ', 'Sim, e tive efeitos adversos.']
      }
    }
    if (question.id === 2) {
      return {
        ...question,
        type: 'options' as const,
        options: Array.from({ length: 11 }, (_, i) => i.toString())
      }
    }
    return question
  })
}

/**
 * Passo de triagem clínica do agendamento branded (Remederi/Vikura).
 * Respondida após a identificação do paciente e antes da escolha do médico;
 * as respostas são salvas soltas (patient_user_id) e vinculadas ao
 * scheduling order pelo backend quando o agendamento é criado.
 */
export default function SchedulingTriageStep({ patientUserId, onComplete, onBack }: SchedulingTriageStepProps) {
  const { config } = useBrand()
  const history = useHistory()
  // Atalho para a Consulta na Hora da marca (ex.: /vikura/consulta-na-hora).
  // Só para marcas com o serviço (Aspaec NÃO tem consulta na hora).
  const showInstantConsultationCta = config.scheduling.hasInstantConsultation === true
  const instantConsultationPath = `${config.pathPrefix ?? ''}/consulta-na-hora`
  const [questions, setQuestions] = useState<TTriageQuestion[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [followUps, setFollowUps] = useState<Record<string, string>>({})
  const [loadingQuestions, setLoadingQuestions] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoadingQuestions(true)
      try {
        const res = await getTriageQuestions()
        const normalized = normalizeQuestions(res)
        setQuestions(normalized)
      } catch {
        toast.error('Erro ao carregar as perguntas da triagem.')
      } finally {
        setLoadingQuestions(false)
      }
    }
    load()
  }, [])

  const showFollowUp = useMemo(() => {
    return (question: TTriageQuestion) => {
      if (!question.follow_up_question || !question.response_question) return false
      return question.response_question.toLowerCase() === (answers[question.id] ?? '').toLowerCase()
    }
  }, [answers])

  const setAnswer = (id: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
  }

  const handleSubmit = async () => {
    const missingRequired = questions.some((question) => !(answers[question.id] ?? '').trim())
    if (missingRequired) {
      toast.error('Responda todas as perguntas da triagem.')
      return
    }

    const missingFollowUp = questions.some(
      (question) => question.id === 4 && showFollowUp(question) && !(followUps[question.id] ?? '').trim()
    )
    if (missingFollowUp) {
      toast.error('Descreva os efeitos adversos que você teve.')
      return
    }

    setSubmitting(true)
    try {
      await api.post('/scheduling/triage-answers', {
        patient_user_id: patientUserId,
        answers: questions.map((question) => ({
          question_id: question.id,
          answer: answers[question.id] ?? '',
          ...(showFollowUp(question) && followUps[question.id] ? { additional_question: followUps[question.id] } : {})
        }))
      })
      onComplete()
    } catch {
      toast.error('Erro ao salvar a triagem. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const renderQuestion = (question: TTriageQuestion) => {
    const answer = answers[question.id] ?? ''

    if (question.type === 'boolean') {
      return (
        <div className="flex gap-2">
          {['Sim', 'Não'].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setAnswer(question.id, option)}
              className={`rounded-full border border-solid px-5 py-2 text-sm font-medium transition-all ${
                answer === option
                  ? 'border-primary-700 bg-primary-700 text-white shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
              }`}>
              {option}
            </button>
          ))}
        </div>
      )
    }

    if (question.type === 'options' && question.options) {
      const isScale = question.id === 2
      return (
        <div className={`flex flex-wrap gap-2 ${isScale ? 'items-center' : 'flex-col items-start md:flex-row'}`}>
          {question.options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setAnswer(question.id, option)}
              className={`rounded-full border border-solid text-sm font-medium transition-all ${
                isScale ? 'h-10 w-10 px-0 py-0' : 'px-4 py-2'
              } ${
                answer === option
                  ? 'border-primary-700 bg-primary-700 text-white shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
              }`}>
              {option.trim()}
            </button>
          ))}
        </div>
      )
    }

    return (
      <textarea
        rows={3}
        value={answer}
        onChange={(event) => setAnswer(question.id, event.target.value)}
        placeholder="Digite sua resposta"
        className="w-full rounded-lg border border-solid border-gray-200 p-3 text-sm text-gray-700 focus:border-primary-700 focus:outline-none"
      />
    )
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-2 border-none bg-transparent p-0 text-sm font-semibold text-neutral-700">
        <ArrowLeft className="size-4" />
        Voltar
      </button>

      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
          <ClipboardList className="h-8 w-8 text-primary-700" />
        </div>
        <h1 className="font-spartan text-2xl font-bold text-gray-800 md:text-3xl">Triagem clínica</h1>
        <p className="max-w-md text-sm text-gray-500 md:text-base">
          Essas respostas ajudam o médico a se preparar para a sua consulta, tornando o atendimento mais seguro e
          direcionado.
        </p>
      </div>

      {/* Card branco de propósito: o theme css das marcas pinta qualquer
          bg-primary-* com a cor cheia, o que engoliria o botão. */}
      {showInstantConsultationCta && (
        <div className="mb-6 flex flex-col items-center justify-between gap-3 rounded-2xl bg-white p-4 shadow-sm md:flex-row">
          <div className="flex items-center gap-3">
            <Zap className="h-6 w-6 shrink-0 text-primary-700" />
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Não quer esperar?</span> Fale com um especialista agora mesmo por
              videochamada, por R$ 99,00.
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              // O paciente acabou de se identificar no agendamento — o Consulta
              // na Hora reaproveita a identificação em vez de pedir de novo.
              history.push(instantConsultationPath, { fromSchedulingIdentification: true })
            }
            className="shrink-0 rounded-lg border-none bg-primary-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-800">
            Consultar agora
          </button>
        </div>
      )}

      {loadingQuestions ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary-700" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {questions.map((question) => (
            <div key={question.id} className="rounded-2xl bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-base font-semibold text-gray-700">
                {question.question}
                <span className="text-red-500"> *</span>
              </h2>
              {renderQuestion(question)}
              {showFollowUp(question) && question.follow_up_question && (
                <div className="mt-4">
                  <p className="mb-2 text-sm font-medium text-gray-600">
                    {question.follow_up_question}
                    {question.id === 4 && <span className="text-red-500"> *</span>}
                  </p>
                  <textarea
                    rows={2}
                    value={followUps[question.id] ?? ''}
                    onChange={(event) => setFollowUps((prev) => ({ ...prev, [question.id]: event.target.value }))}
                    placeholder="Digite sua resposta"
                    className="w-full rounded-lg border border-solid border-gray-200 p-3 text-sm text-gray-700 focus:border-primary-700 focus:outline-none"
                  />
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:opacity-60">
            {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
            Salvar e continuar
          </button>
        </div>
      )}
    </div>
  )
}
